import { useEffect, useState } from "react";
import { mntAPI, fmtINR, PRIO_STYLE, STATUS_STYLE } from "./mntApi";
import { useNavigate } from "react-router-dom";
import CountUp from "@/pages/operations/CountUp";
import {
  Boxes, AlertTriangle, ClipboardList, Timer, Users, CalendarClock,
  ShieldAlert, FileCheck, Zap, Flame,
} from "lucide-react";

export default function MaintenanceDashboard() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    const load = () => mntAPI.dashboard().then(setData).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const k = data?.kpis || {};
  const tiles = [
    { label: "Assets",              value: k.assets_total,         icon: Boxes,          accent: "text-slate-700 bg-slate-50" },
    { label: "Open Work Orders",    value: k.wo_open,              icon: ClipboardList,  accent: "text-sky-700 bg-sky-50" },
    { label: "Critical",            value: k.wo_critical,          icon: Flame,          accent: "text-rose-700 bg-rose-50" },
    { label: "SLA Breaches",        value: k.wo_breaches,          icon: ShieldAlert,    accent: "text-amber-700 bg-amber-50" },
    { label: "Engineers Working",   value: k.engineers_working,    icon: Users,          accent: "text-violet-700 bg-violet-50" },
    { label: "Preventive Due",      value: k.pm_due,               icon: CalendarClock,  accent: "text-emerald-700 bg-emerald-50" },
    { label: "Completed Today",     value: k.wo_completed_today,   icon: FileCheck,      accent: "text-cyan-700 bg-cyan-50" },
    { label: "MTTR (min)",          value: k.mttr_minutes ?? "—",  icon: Timer,          accent: "text-slate-700 bg-slate-50", raw: true },
  ];

  return (
    <div className="space-y-5" data-testid="mnt-dashboard">
      {/* KPI GRID */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        {tiles.map(t => {
          const Icon = t.icon;
          return (
            <div key={t.label} data-testid={`mnt-kpi-${t.label.toLowerCase().replace(/\s+/g,"-").replace(/[^a-z0-9-]/g,"")}`}
              className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className={`w-8 h-8 rounded-lg ${t.accent} flex items-center justify-center mb-2.5`}>
                <Icon className="w-4 h-4" strokeWidth={1.75}/>
              </div>
              <div className="font-display text-[24px] font-semibold text-hocp-navy tabular-nums leading-none">
                {t.raw ? t.value : <CountUp value={Number(t.value) || 0}/>}
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5 uppercase tracking-wider">{t.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* RECENT WORK ORDERS */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-display text-[15px] font-semibold text-hocp-navy">Recent Work Orders</h2>
              <p className="text-[12px] text-slate-500">Latest 8 across all statuses.</p>
            </div>
            <button onClick={() => nav("/maintenance/work-orders")} data-testid="mnt-dash-view-all-wos"
                    className="text-[12px] text-hocp-navy font-medium hover:underline">View all →</button>
          </div>
          {!data?.recent_wos?.length ? (
            <div className="p-8 text-center text-[13px] text-slate-400">No work orders yet.</div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">Code</th>
                  <th className="text-left px-4 py-2.5">Title</th>
                  <th className="text-left px-4 py-2.5">Priority</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Asset</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.recent_wos.map(w => {
                  const p = PRIO_STYLE[w.priority] || PRIO_STYLE.medium;
                  const s = STATUS_STYLE[w.status] || STATUS_STYLE.submitted;
                  return (
                    <tr key={w.id} onClick={() => nav("/maintenance/work-orders")}
                        data-testid={`mnt-dash-wo-${w.id}`}
                        className="hover:bg-slate-50/60 cursor-pointer">
                      <td className="px-4 py-3 mono text-slate-500">{w.code}</td>
                      <td className="px-4 py-3 font-medium text-hocp-navy truncate max-w-[280px]">{w.title}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${p.chip.replace("ring-1", "").replace(/ring-\S+/g,"")}`}>{p.label}</span></td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${s.chip}`}>{s.label}</span></td>
                      <td className="px-4 py-3 text-slate-600 truncate max-w-[180px]">{w.asset_snapshot?.name || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* AMCs EXPIRING */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-display text-[15px] font-semibold text-hocp-navy flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500"/> AMCs Expiring Soon
            </h2>
            <p className="text-[12px] text-slate-500">Within the next 60 days.</p>
          </div>
          {!data?.amcs_expiring?.length ? (
            <div className="p-8 text-center text-[12.5px] text-slate-400">Nothing expiring soon.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.amcs_expiring.map(a => (
                <div key={a.id} data-testid={`mnt-dash-amc-${a.id}`} className="px-5 py-3">
                  <div className="text-[13px] font-medium text-hocp-navy truncate">{a.title}</div>
                  <div className="text-[11.5px] text-slate-500 mt-0.5">
                    {a.vendor_name || "—"} · <span className="mono">ends {a.end_date}</span>
                    {a.annual_cost && <span> · {fmtINR(a.annual_cost)}/yr</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* STATUS BREAKDOWN CHIPS */}
      {data?.wo_by_status && Object.keys(data.wo_by_status).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-display text-[15px] font-semibold text-hocp-navy mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500"/> Work Order Distribution
          </h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.wo_by_status).map(([status, count]) => {
              const s = STATUS_STYLE[status] || STATUS_STYLE.submitted;
              return (
                <div key={status} className={`px-3 py-2 rounded-lg ${s.chip} flex items-center gap-2`}>
                  <span className="text-[11px] font-medium">{s.label}</span>
                  <span className="text-[15px] font-semibold tabular-nums">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
